import {
  Component, OnInit, Injector, ComponentRef, Renderer2, NgZone, Inject, OnDestroy
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector, Portal } from '@angular/cdk/portal';
import {
  Subject, mergeMap, catchError
} from 'rxjs';

import { GtmDataLayerService, GtmCustomEvents } from '@sondermind/google-tag-manager';
import { UserSystemInfoService } from '@sondermind/utilities/services';

import {
  buildIntakeEndpoint,
  HOST_OVERLAY,
  WizardService,
} from '@sondermind/flows-host';

import { FlowsLaunchDarklyService, LAUNCH_DARKLY_SERVICE } from '@sondermind/launch-darkly';
import { BrowserNavButtonsService } from '@sondermind/utilities/browser-nav-buttons';

import { ResumeComponent } from '../resume/resume.component';
import { HostRouterComponent } from '../host-router/host-router.component';

@Component({
  selector: 'sonder-flow-boot',
  template: ''
})
export class AppBootComponent implements OnInit, OnDestroy {
  slug: string;
  // slug that comes from LaunchDarkly
  ldSlugLoaded: boolean = false;
  // needed to compare and prevent non-default flows from being overriden by LaunchDarkly tests
  defaultMatchFlow: string = 'matching-flow-base';
  canOverrideBaseFlow: boolean = false;
  private flowsSupportingNavButtons: string[] = [
    'matching-flow-base',
    'matching-flow-base-short',
    'directory-matching-flow'
  ];

  hostOverlay: OverlayRef;
  hostPortal: Portal<ComponentRef<HostRouterComponent>>;
  resumeOverlay: OverlayRef;
  resumePortal: Portal<ComponentRef<ResumeComponent>>;
  overlayInjector: PortalInjector;
  private matchFlowLoadedEventSubject: Subject<string> = new Subject();

  constructor(
    @Inject(DOCUMENT)
    private document: Document,
    private renderer: Renderer2,
    private overlay: Overlay,
    private zone: NgZone,
    private wizardSvc: WizardService,
    private injector: Injector,
    private gtmDataLayerService: GtmDataLayerService,
    @Inject(LAUNCH_DARKLY_SERVICE)
    private launchDarklyService: FlowsLaunchDarklyService,
    private userSystemInfoService: UserSystemInfoService,
    private browserNavButtonsService: BrowserNavButtonsService
  ) {
    const root = this.renderer.selectRootElement('sonder-flow-boot') as Element;
    this.slug = root.getAttribute('slug');
  }

  ngOnInit(): void {
    this.canOverrideBaseFlow = this.slug === this.defaultMatchFlow;

    // the container that owns the hide/resume button
    this.resumeOverlay = this.overlay.create({
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: ['flowsResumePanel', 'overlay']
    });

    // the modal container that owns the wizard parts
    this.hostOverlay = this.overlay.create({
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      panelClass: ['flowsHostPanel', 'sonder-formlib-light-background']
    });

    // build the portals! the overlays
    this.overlayInjector = new PortalInjector(this.injector, new WeakMap([[HOST_OVERLAY, this.hostOverlay]]));
    this.hostPortal = new ComponentPortal(HostRouterComponent, null, this.overlayInjector);
    this.resumePortal = new ComponentPortal(ResumeComponent, null, this.overlayInjector);

    this.initializeExternalEvents();

    this.wizardSvc.toggleRunning(true);
    this.initializeMatchFlow();

    if (this.flowsSupportingNavButtons.includes(this.slug)) {
      this.browserNavButtonsService.init();
      this.wizardSvc.attachBrowserButtonService(this.browserNavButtonsService);
    }
  }

  ngOnDestroy(): void {
    this.launchDarklyService.closeClient();
    // even-though browserNavButtonsService init() is called conditionally,
    //  it is safe to unconditionally call destroy()
    this.browserNavButtonsService.destroy();
    this.wizardSvc.detachBrowserButtonService();
  }

  resumeFlow(): void {
    if (!this.hostOverlay.hasAttached()) {
      this.hostOverlay.attach(this.hostPortal);
    }

    if (!this.resumeOverlay.hasAttached()) {
      this.resumeOverlay.attach(this.resumePortal);
    }
  }

  pauseFlow(): void {
    // the host component destroys itself so it can use animations
  }

  /**
   * Queries for all elements containing the `data-sonder-flow-trigger` then starts event listeners on each of these.
   * When acted upon, which is typically a "click" event, the match flow is initialized if it has not already been
   * inside of the NgZone. Otherwise, it handles opening and closing the match flow window.
   *
   * @private
   * @memberof AppBootComponent
   */
  private initializeExternalEvents() {
    const nodes = this.document.querySelectorAll('[data-sonder-flow-trigger]');
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes.item(i);
      const etype = node.getAttribute('data-sonder-flow-trigger');

      node.addEventListener(etype, (event) => {
        event.preventDefault();
        this.zone.run(() => {
          if (!this.wizardSvc.initialized) {
            this.wizardSvc.toggleRunning(true);
            this.initializeMatchFlow();
          } else if (this.wizardSvc.started) {
            this.wizardSvc.toggleRunning(true);
          } else {
            this.wizardSvc.start().subscribe(() => {
              this.wizardSvc.toggleRunning(true);
            });
          }
        });
      });
    }
  }

  private getSlugQueryParameterValue(): string {
    const urlParams = new URLSearchParams(window.location.search);

    return urlParams.get('slug');
  }

  /**
   * Initializing the match flow using either
   * the default slug (supplied via "slug" attribute of "sonder-flow-boot" element)
   * or an override value from LaunchDarkly
   *
   * @private
   * @memberof AppBootComponent
   */
  private initializeMatchFlow(): void {
    this.setMatchFlowDefaultOnDataLayer();
    const matchFlowInitAttempt = this.matchFlowLoadedEventSubject.asObservable().pipe(
      mergeMap((matchFlowSlug) => this.wizardSvc.initialize(matchFlowSlug, { endpoint_builder: buildIntakeEndpoint })),
      catchError(() => this.wizardSvc.initialize(this.slug, { endpoint_builder: buildIntakeEndpoint }))
    );

    // set up wizard initialization subscription
    //   so can react and 'start' a flow
    matchFlowInitAttempt.subscribe(() => {
      // if already started (has data) short circuit start
      // the form field will be hidden, so we won't get 'startFlow'
      if (this.wizardSvc.started) {
        this.resumeOverlay.attach(this.resumePortal);
        this.wizardSvc.start().subscribe();
      }

      // hide/show host on running changes
      this.wizardSvc.running$.subscribe((running) => {
        running ? this.resumeFlow() : this.pauseFlow();
      });

      // NB: launchDarklyService.initClient() will be called before
      //     reaching this block of code
      const matchFlowInitData = [
        ...this.launchDarklyService.getInitData(),
        { human: 'Matchflow Domain', key: 'domain', value: window.location.hostname },
        { human: 'Document Referrer', key: 'referrer', value: document.referrer },
        { human: 'Path Name', key: 'pathname', value: window.location.pathname },
      ];
      const gaId = this.getGoogleAnalyticsClientId();
      if (gaId) {
        matchFlowInitData.push({ human: 'GA Client ID', key: 'ga_id', value: gaId });
      }

      this.wizardSvc.start(matchFlowInitData).subscribe(() => {
        this.wizardSvc.toggleRunning(true);
      });
    });

    this.launchDarklyService.initClient().subscribe(() => {
      // If there is the slug query string parameter passed in, it automatically uses that one
      const slugQueryParameterValue = this.getSlugQueryParameterValue();
      if (slugQueryParameterValue) {
        this.matchFlowLoadedEventSubject.next(slugQueryParameterValue);
        return;
      }

      // Check if LaunchDarkly is controlling what slug to load, and use it if available
      // NOTE: currently, LaunchDarkly is only allowed to override the base v8 flow launched from [dotcom]/match
      // flows such the one launched from [dotcom]/psychiatry-match are prevented from
      if (this.canOverrideBaseFlow && this.launchDarklyService.ldSlug?.length > 0 && !this.ldSlugLoaded) {
        this.ldSlugLoaded = true;
        this.matchFlowLoadedEventSubject.next(this.launchDarklyService.ldSlug);
      } else {
        // trigger initialization from slug attribute
        this.matchFlowLoadedEventSubject.next(this.slug);
      }
    });
  }

  /**
   * Method for setting the match flow default custom event with user system metadata.
   *
   * @private
   * @memberof AppBootComponent
   */
  private setMatchFlowDefaultOnDataLayer(): void {
    const browserInfo = this.userSystemInfoService.userSystemInfo.browser;
    const platformInfo = this.userSystemInfoService.userSystemInfo.platform;
    /* eslint-disable no-extra-boolean-cast */
    const browser = !!browserInfo ? `${browserInfo.name} ${browserInfo.version}` : '';
    const platform = !!platformInfo ? `${platformInfo.type} ${platformInfo.vendor}` : '';
    /* eslint-enable no-extra-boolean-cast */

    this.gtmDataLayerService.dataLayer = {
      event: GtmCustomEvents.MATCH_FLOW_DEFAULT,
      category: GtmCustomEvents.MATCH_FLOW_DEFAULT,
      action: browser,
      label: platform
    };
  }

  private getGoogleAnalyticsClientId(): string {
    // Regex to get the _ga cookie value from the document.cookie string
    let match = document.cookie.match('(?:^|;)\\s*_ga=([^;]*)');
    const raw = match ? decodeURIComponent(match[1]) : null;
    if (raw) {
      match = raw.match(/(\d+\.\d+)$/)
    }
    return match ? match[1] : null;
  }
}
